import { useEffect } from 'react';

export interface PageSEOConfig {
  title: string;
  description: string;
  canonicalUrl?: string;
  keywords?: string[];
  ogImage?: string;
  twitterImage?: string;
  additionalMeta?: Record<string, string>;
  structuredData?: Record<string, unknown>;
}

type MetaSelector = { name?: string; property?: string };

const ensureMetaTag = (selector: MetaSelector): HTMLMetaElement => {
  const attribute = selector.name ? 'name' : 'property';
  const value = selector.name ?? selector.property ?? '';
  const existing = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${value}"]`);

  if (existing) {
    return existing;
  }

  const meta = document.createElement('meta');
  if (selector.name) {
    meta.setAttribute('name', selector.name);
  }
  if (selector.property) {
    meta.setAttribute('property', selector.property);
  }
  document.head.appendChild(meta);
  return meta;
};

const ensureCanonicalLink = (): HTMLLinkElement => {
  const existing = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (existing) {
    return existing;
  }

  const link = document.createElement('link');
  link.setAttribute('rel', 'canonical');
  document.head.appendChild(link);
  return link;
};

const ensureStructuredDataScript = (): HTMLScriptElement => {
  const existing = document.head.querySelector<HTMLScriptElement>('script[data-seo="structured-data"]');
  if (existing) {
    return existing;
  }

  const script = document.createElement('script');
  script.setAttribute('type', 'application/ld+json');
  script.setAttribute('data-seo', 'structured-data');
  document.head.appendChild(script);
  return script;
};

interface StoredMetaState {
  element: HTMLMetaElement;
  previousContent: string | null;
}

export const usePageSEO = (config: PageSEOConfig | null | undefined): void => {
  useEffect(() => {
    if (!config) {
      return;
    }

    const {
      title,
      description,
      canonicalUrl,
      keywords,
      ogImage,
      twitterImage,
      additionalMeta,
      structuredData,
    } = config;

    const previousTitle = document.title;
    document.title = title;

    const managedMetaSelectors: Array<{ selector: MetaSelector; content: string | undefined }> = [
      { selector: { name: 'description' }, content: description },
      { selector: { name: 'keywords' }, content: keywords?.join(', ') },
      { selector: { property: 'og:type' }, content: 'website' },
      { selector: { property: 'og:title' }, content: title },
      { selector: { property: 'og:description' }, content: description },
      { selector: { property: 'og:url' }, content: canonicalUrl },
      { selector: { property: 'og:image' }, content: ogImage ?? twitterImage },
      { selector: { property: 'og:site_name' }, content: 'Wallstreetle' },
      { selector: { property: 'twitter:card' }, content: 'summary_large_image' },
      { selector: { property: 'twitter:title' }, content: title },
      { selector: { property: 'twitter:description' }, content: description },
      { selector: { property: 'twitter:url' }, content: canonicalUrl },
      { selector: { property: 'twitter:image' }, content: twitterImage ?? ogImage },
    ];

    if (additionalMeta) {
      Object.entries(additionalMeta).forEach(([key, value]) => {
        managedMetaSelectors.push({ selector: { name: key }, content: value });
      });
    }

    const storedMetaStates: StoredMetaState[] = managedMetaSelectors.map(({ selector, content }) => {
      const element = ensureMetaTag(selector);
      const previousContent = element.getAttribute('content');
      if (content !== undefined && content !== null) {
        element.setAttribute('content', content);
      } else {
        element.removeAttribute('content');
      }
      return { element, previousContent };
    });

    const canonicalLink = ensureCanonicalLink();
    const previousCanonical = canonicalLink.getAttribute('href');
    if (canonicalUrl) {
      canonicalLink.setAttribute('href', canonicalUrl);
    } else {
      canonicalLink.removeAttribute('href');
    }

    const structuredDataScript = ensureStructuredDataScript();
    const previousStructuredData = structuredDataScript.textContent;
    if (structuredData) {
      structuredDataScript.textContent = JSON.stringify(structuredData, null, 2);
    } else {
      structuredDataScript.textContent = '';
    }

    return () => {
      document.title = previousTitle;
      storedMetaStates.forEach(({ element, previousContent }) => {
        if (previousContent === null || previousContent === undefined || previousContent === '') {
          element.removeAttribute('content');
        } else {
          element.setAttribute('content', previousContent);
        }
      });

      if (previousCanonical) {
        canonicalLink.setAttribute('href', previousCanonical);
      } else {
        canonicalLink.removeAttribute('href');
      }

      if (previousStructuredData) {
        structuredDataScript.textContent = previousStructuredData;
      } else {
        structuredDataScript.textContent = '';
      }
    };
  }, [config]);
};
